import { useDashboardMetric } from "../DashboardCache";
import * as React from "react";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import DashboardIcon from "@mui/icons-material/Dashboard";
import BeachAccessIcon from "@mui/icons-material/BeachAccess";
import OtherHousesIcon from "@mui/icons-material/OtherHouses";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import FamilyRestroomIcon from "@mui/icons-material/FamilyRestroom";
import CarCrashIcon from "@mui/icons-material/CarCrash";
import { Badge, Box, Collapse } from "@mui/material";
import jwt_decode from "jwt-decode";
import { ISignInData } from "../../../services/API/signin";
import useToken from "../../useToken";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import BookOnlineIcon from "@mui/icons-material/BookOnline";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import PaymentsIcon from "@mui/icons-material/Payments";
import Groups2Icon from "@mui/icons-material/Groups2";
import SpaceDashboardOutlinedIcon from "@mui/icons-material/SpaceDashboardOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import AssignmentLateOutlinedIcon from "@mui/icons-material/AssignmentLateOutlined";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import { getPolicyLinkOverview } from "../../../services/API/PolicyLinkRequests";
import { getClaimIntakeOverview } from "../../../services/API/MobileClaimIntakes";
import { getQuoteAttentionCount } from "../../../services/API/QuoteRequests";
import { canAccessExpenses } from "../Expense/ExpenseData";
// Lazy-loaded feature components to reduce initial bundle size
const DashboardPreview = React.lazy(() => import("../DashboardPreview"));
const MedicalInsurance = React.lazy(
  () => import("../Medical/MedicalInsurance")
);
const FambulInsurance = React.lazy(() => import("../Fambul/FambulInsurance"));
const MotorInsurance = React.lazy(() => import("../Motor/MotorInsurance"));
const GroupLifeInsurance = React.lazy(
  () => import("../GroupLife/GroupLifeInsurance")
);
const OtherInsurance = React.lazy(
  () => import("../OtherInsurance/OtherInsurance")
);
const FrontDeskPayment = React.lazy(
  () => import("../FrontDeskPayment/FrontDeskPayment")
);
const Expenses = React.lazy(() => import("../Expense/Expenses"));
const CustomerList = React.lazy(() => import("../MoneyBack/CustomerList"));
const CustomerOverview = React.lazy(() => import("../Customers/CustomerOverview"));
const PolicyLinkRequests = React.lazy(() => import("../Customers/PolicyLinkRequests"));
const LinkedPolicies = React.lazy(() => import("../Customers/LinkedPolicies"));
const UnmatchedClaimIntakes = React.lazy(() => import("../Customers/UnmatchedClaimIntakes"));
const QuoteRequests = React.lazy(() => import("../Customers/QuoteRequests"));
interface IMainListItemsProps {
  setButtonClick: (button: JSX.Element) => void;
}

export interface ISideCounts {
  medical?: number;
  fambul?: number;
  motor?: number;
  groupLife?: number;
  others?: number;
  frontDesk?: number;
  claimsNeedAction?: number;
  expense?: number;
  pendingPolicyLinkRequests?: number;
  claimIntakesNeedingReview?: number;
  quoteRequestsNeedingReview?: number;
}

const MainListItems = (props: IMainListItemsProps) => {
  const token = useToken().token;
  const { data: dashboardSnapshot } = useDashboardMetric("dashboardData");
  const userData: ISignInData = jwt_decode(token);
  const [counts, setCounts] = React.useState<ISideCounts>({
    medical: 0,
    fambul: 0,
    motor: 0,
    groupLife: 0,
    others: 0,
    frontDesk: 0,
    expense: 0,
  });
  const [openExpense, setOpenExpense] = React.useState(false);
  const [openCustomers, setOpenCustomers] = React.useState(false);
  const customerNavItemSx = {
    minHeight: 38,
    px: 1.25,
    py: 0.5,
    mx: 0.75,
    my: 0.25,
    borderRadius: 1.5,
    color: "text.secondary",
    "&:hover": { bgcolor: "action.hover", color: "primary.main", transform: "translateX(2px)" },
    transition: "background-color 160ms ease, color 160ms ease, transform 160ms ease",
  } as const;


  React.useEffect(() => {
    setCounts(current => ({ ...current, ...dashboardSnapshot }));
  }, [dashboardSnapshot]);


  React.useEffect(() => {
    if (userData.CanReviewCustomerPolicyLinks === "True") {
      Promise.all([getPolicyLinkOverview(token), getClaimIntakeOverview(token)])
        .then(([policyLinks, claimIntakes]) => setCounts((current) => ({
          ...current,
          pendingPolicyLinkRequests: policyLinks.pendingPolicyLinks,
          claimIntakesNeedingReview: claimIntakes.claimIntakesNeedingReview,
        })))
        .catch(() => undefined);
    }
    if (Number(userData.CanViewInsurance) === 1) {
      getQuoteAttentionCount(token).then((quotes) => setCounts((current) => ({
        ...current,
        quoteRequestsNeedingReview: quotes.quoteRequestsNeedingReview,
      }))).catch(() => undefined);
    }
  }, [token, userData.CanReviewCustomerPolicyLinks, userData.CanViewInsurance]);

  const customerActions = (counts.pendingPolicyLinkRequests ?? 0) + (counts.claimIntakesNeedingReview ?? 0) + (counts.quoteRequestsNeedingReview ?? 0);

  const loadOthers = () => {
    props.setButtonClick(<OtherInsurance />);
  };
  const loadGroupLife = () => {
    props.setButtonClick(<GroupLifeInsurance />);
  };
  const loadMotor = () => {
    props.setButtonClick(<MotorInsurance />);
  };
  const loadMoneyBack = () => {
    props.setButtonClick(<CustomerList />);
  };
  const loadFambul = () => {
    props.setButtonClick(<FambulInsurance />);
  };
  const loadMed = () => {
    props.setButtonClick(<MedicalInsurance />);
  };
  const loadDash = () => {
    props.setButtonClick(<DashboardPreview />);
  };
  const loadPayment = () => {
    props.setButtonClick(<FrontDeskPayment />);
  };
  const loadExpenses = (type: number) => {
    props.setButtonClick(<Expenses type={type} />);
  };
  const loadCustomerOverview = () => props.setButtonClick(<CustomerOverview onOpenQuoteRequests={loadQuoteRequests} />);
  const loadPolicyLinks = () => props.setButtonClick(<PolicyLinkRequests />);
  const loadLinkedPolicies = () => props.setButtonClick(<LinkedPolicies />);
  const loadUnmatchedClaimIntakes = () => props.setButtonClick(<UnmatchedClaimIntakes />);
  const loadQuoteRequests = () => props.setButtonClick(<div style={{ marginTop: 80, marginRight: 230, paddingLeft: 24 }}><QuoteRequests /></div>);

  const openExpenseDrawer = () => {
    if (openExpense) {
      setOpenExpense(false);
    } else {
      setOpenExpense(true);
      setOpenCustomers(false);
    }
  };
  const openCustomerDrawer = () => {
    if (openCustomers) {
      setOpenCustomers(false);
    } else {
      setOpenCustomers(true);
      setOpenExpense(false);
    }
  };
  return (
    <Box sx={{ "& .MuiListItemButton-root": { mx: .5, borderRadius: 1.5, transition: "background-color 160ms ease, transform 160ms ease, box-shadow 160ms ease" }, "& .MuiListItemButton-root:hover": { bgcolor: "action.selected", transform: "translateX(2px)", boxShadow: 1 }, "& .MuiListItemButton-root:active": { bgcolor: "action.selected", boxShadow: 2 } }}>
      <React.Fragment>
        <ListItemButton onClick={loadDash} sx={{ paddingBottom: 0 }}>
          <ListItemIcon>
            <DashboardIcon
              sx={{ color: (theme) => theme.palette.primary.light }}
            />
          </ListItemIcon>
          <ListItemText primary="Dashboard" />
        </ListItemButton>
        {userData.CanViewMedicalInsurance === "True" && (
          <ListItemButton onClick={loadMed} sx={{ paddingBottom: 0 }}>
            <ListItemIcon>
              <Badge badgeContent={counts.medical} color="error">
                <MedicalServicesIcon
                  sx={{ color: (theme) => theme.palette.info.dark }}
                />
              </Badge>
            </ListItemIcon>
            <ListItemText primary="Medical" />
          </ListItemButton>
        )}
        {userData.CanViewMotorInsurance === "True" && (
          <ListItemButton onClick={loadMotor} sx={{ paddingBottom: 0 }}>
            <ListItemIcon>
              <Badge badgeContent={counts.motor} color="error">
                <CarCrashIcon
                  sx={{ color: (theme) => theme.palette.warning.main }}
                />
              </Badge>
            </ListItemIcon>
            <ListItemText primary="Motor" />
          </ListItemButton>
        )}
        <ListItemButton onClick={loadMoneyBack} sx={{ paddingBottom: 0 }}>
          <ListItemIcon>
            <Badge>
              <PaymentsIcon
                sx={{ color: (theme) => theme.palette.success.main }}
              />
            </Badge>
          </ListItemIcon>
          <ListItemText primary="Money Back" />
        </ListItemButton>
        {Number(userData.CanViewInsurance) === 1 && (
          <>
            <ListItemButton onClick={loadFambul} sx={{ paddingBottom: 0 }}>
              <ListItemIcon>
                <Badge badgeContent={counts.fambul} color="error">
                  <FamilyRestroomIcon
                    sx={{ color: (theme) => theme.palette.secondary.main }}
                  />
                </Badge>
              </ListItemIcon>
              <ListItemText primary="Fambul" />
            </ListItemButton>

            <ListItemButton onClick={loadGroupLife} sx={{ paddingBottom: 0 }}>
              <ListItemIcon>
                <Badge badgeContent={counts.groupLife} color="error">
                  <BeachAccessIcon
                    sx={{ color: (theme) => theme.palette.primary.light }}
                  />
                </Badge>
              </ListItemIcon>
              <ListItemText primary="Group Life" />
            </ListItemButton>
          </>
        )}
        {(userData.CanReviewCustomerPolicyLinks === "True" || Number(userData.CanViewInsurance) === 1) && (
          <>
            <ListItemButton onClick={openCustomerDrawer} sx={{ mt: 0.5, py: 1, borderRadius: 1.5, bgcolor: openCustomers ? "action.selected" : "transparent", borderLeft: 3, borderColor: "secondary.main", boxShadow: openCustomers ? 2 : 0, "&:hover": { bgcolor: "action.selected" } }}>
              <ListItemIcon><Badge badgeContent={customerActions} color="error"><Groups2Icon sx={{ color: (theme) => theme.palette.secondary.dark }} /></Badge></ListItemIcon>
              <ListItemText primary="Customer" primaryTypographyProps={{ fontWeight: 600 }} />
              {openCustomers ? <ExpandMore /> : <ExpandLess />}
            </ListItemButton>
            <Collapse in={openCustomers} timeout="auto" unmountOnExit sx={{ ml: 2.5, mt: 0.5, mb: 1, py: 0.5, borderLeft: 1, borderColor: "divider", bgcolor: "action.hover", borderRadius: "0 8px 8px 0" }}>
              <ListItemButton onClick={loadCustomerOverview} sx={customerNavItemSx}><ListItemIcon sx={{ minWidth: 30 }}><SpaceDashboardOutlinedIcon fontSize="small" /></ListItemIcon><ListItemText primary="Customer Overview" primaryTypographyProps={{ fontSize: "0.8rem", fontWeight: 500 }} /></ListItemButton>
              <ListItemButton onClick={loadPolicyLinks} sx={customerNavItemSx}><ListItemIcon sx={{ minWidth: 30 }}><LinkOutlinedIcon fontSize="small" /></ListItemIcon><ListItemText primary="Policy Link Requests" primaryTypographyProps={{ fontSize: "0.8rem", fontWeight: 500 }} /></ListItemButton>
              <ListItemButton onClick={loadLinkedPolicies} sx={customerNavItemSx}><ListItemIcon sx={{ minWidth: 30 }}><VerifiedUserOutlinedIcon fontSize="small" /></ListItemIcon><ListItemText primary="Linked Policies" primaryTypographyProps={{ fontSize: "0.8rem", fontWeight: 500 }} /></ListItemButton>
              <ListItemButton onClick={loadUnmatchedClaimIntakes} sx={customerNavItemSx}><ListItemIcon sx={{ minWidth: 30 }}><AssignmentLateOutlinedIcon fontSize="small" /></ListItemIcon><ListItemText primary="Unmatched Claims" primaryTypographyProps={{ fontSize: "0.8rem", fontWeight: 500 }} /></ListItemButton>
              {Number(userData.CanViewInsurance) === 1 ? <ListItemButton onClick={loadQuoteRequests} sx={customerNavItemSx}><ListItemIcon sx={{ minWidth: 30 }}><Badge badgeContent={counts.quoteRequestsNeedingReview} color="error"><RequestQuoteOutlinedIcon fontSize="small" /></Badge></ListItemIcon><ListItemText primary="Quote Requests" primaryTypographyProps={{ fontSize: "0.8rem", fontWeight: 500 }} /></ListItemButton> : null}
            </Collapse>
          </>
        )}
        {Number(userData.CanManageOtherInsurance) === 1 && (
          <>
            <ListItemButton onClick={loadOthers} sx={{ paddingBottom: 0 }}>
              <ListItemIcon>
                <Badge badgeContent={counts.others} color="error">
                  <OtherHousesIcon
                    sx={{ color: (theme) => theme.palette.error.main }}
                  />
                </Badge>
              </ListItemIcon>
              <ListItemText primary="Others" />
            </ListItemButton>
          </>
        )}
        {canAccessExpenses(userData) && (
          <>
            <ListItemButton
              onClick={openExpenseDrawer}
              sx={{ mt: 0.5, py: 1, borderRadius: 1.5, bgcolor: openExpense ? "action.selected" : "transparent", borderLeft: 3, borderColor: "info.main", boxShadow: openExpense ? 2 : 0, "&:hover": { bgcolor: "action.selected" } }}
            >
              <ListItemIcon>
                <Badge badgeContent={counts.expense} color="error">
                  <BookOnlineIcon
                    sx={{ color: (theme) => theme.palette.info.dark }}
                  />
                </Badge>
              </ListItemIcon>
              <ListItemText primary="Expenses" primaryTypographyProps={{ fontWeight: 600 }} />
              {openExpense ? <ExpandMore /> : <ExpandLess />}
            </ListItemButton>
            <Collapse
              in={openExpense}
              timeout="auto"
              unmountOnExit
              sx={{ paddingLeft: "25px" }}
            >
              <ListItemButton
                onClick={(e) => loadExpenses(0)}
                sx={{ paddingLeft: 0, paddingRight: 0, paddingBottom: 0 }}
              >
                <ListItemText
                  primary="All"
                  primaryTypographyProps={{ style: { fontSize: "0.775rem" } }}
                  sx={{ marginLeft: "4px" }}
                />
              </ListItemButton>
              <ListItemButton
                sx={{ paddingLeft: 0, paddingRight: 0, paddingBottom: 0 }}
                onClick={(e) => loadExpenses(1)}
              >
                <ListItemText
                  primary="Building"
                  primaryTypographyProps={{ style: { fontSize: "0.775rem" } }}
                  sx={{ marginLeft: "4px" }} // Adjust this value as needed
                />
              </ListItemButton>
              <ListItemButton
                sx={{ paddingLeft: 0, paddingRight: 0, paddingBottom: 0 }}
                onClick={(e) => loadExpenses(8)}
              >
                <ListItemText
                  primary="LED Imprest"
                  primaryTypographyProps={{ style: { fontSize: "0.775rem" } }}
                  sx={{ marginLeft: "4px" }}
                />
              </ListItemButton>
              <ListItemButton
                sx={{ paddingLeft: 0, paddingRight: 0, paddingBottom: 0 }}
                onClick={(e) => loadExpenses(2)}
              >
                <ListItemText
                  primary="Office Expenditure - Life"
                  primaryTypographyProps={{ style: { fontSize: "0.775rem" } }}
                />
              </ListItemButton>
              <ListItemButton
                sx={{ paddingLeft: 0, paddingRight: 0, paddingBottom: 0 }}
                onClick={(e) => loadExpenses(5)}
              >
                <ListItemText
                  primary="Office Expenditure Non-Life"
                  primaryTypographyProps={{ style: { fontSize: "0.775rem" } }}
                />
              </ListItemButton>
              <ListItemButton
                sx={{ paddingLeft: 0, paddingRight: 0, paddingBottom: 0 }}
                onClick={(e) => loadExpenses(3)}
              >
                <ListItemText
                  primary="Office Expenditure General - NL"
                  primaryTypographyProps={{ style: { fontSize: "0.775rem" } }}
                />
              </ListItemButton>
              <ListItemButton
                sx={{ paddingLeft: 0, paddingRight: 0, paddingBottom: 0 }}
                onClick={(e) => loadExpenses(4)}
              >
                <ListItemText
                  primary="Single Cheque Voucher"
                  primaryTypographyProps={{ style: { fontSize: "0.775rem" } }}
                />
              </ListItemButton>
            </Collapse>
          </>
        )}
        {userData.CanViewPaymentReceipt === "True" && (
          <ListItemButton onClick={loadPayment}>
            <ListItemIcon>
              <Badge badgeContent={counts.frontDesk} color="error">
                <AttachMoneyIcon
                  sx={{ color: (theme) => theme.palette.success.dark }}
                />
              </Badge>
            </ListItemIcon>
            <ListItemText primary="F-Desk Payment" />
          </ListItemButton>
        )}
      </React.Fragment>
    </Box>
  );
};
export default MainListItems;
